import { useMemo } from 'react';
import { Badge } from '../../Badge/Badge';
import { Select } from '../../Select/Select';
import type { SelectOption } from '../../Select/Select.types';
import { useFinancialInstrumentTranslation } from './i18n';
import type { InstrumentTypeOption } from './types';

export interface InstrumentTypeFieldProps {
  /** Valor seleccionado (UUID del tipo). */
  value: string;
  /** Tipos de instrumento disponibles (catálogo GAP-2). */
  options: InstrumentTypeOption[];
  /**
   * `true` si el usuario tiene `change_default_financial_instrument`: el campo es
   * un `Select` editable. Sin el permiso (o sin catálogo) cae a chip read-only
   * con el nombre del tipo (FR-84 / 105-17.02).
   */
  canChangeDefaultType: boolean;
  /** Nombre legible del tipo seleccionado (default de la condición — GAP-3/GAP-4). */
  selectedTypeName?: string;
  /** El catálogo se está cargando. */
  isLoading?: boolean;
  /** Marca el error de "requerido" (formulario tocado y sin valor). */
  error?: boolean;
  /** Deshabilita el `Select` (documento read-only). */
  disabled?: boolean;
  onChange: (value: string) => void;
}

const DASH = '—';

/**
 * Campo "Tipo de instrumento" (105-17.02).
 *
 * Con `change_default_financial_instrument` y catálogo disponible: `Select` de
 * tipos activos. Sin el permiso (o sin catálogo — GAP-2): chip read-only con el
 * nombre del tipo default; el valor igual viaja en el submit del formulario.
 */
export function InstrumentTypeField({
  value,
  options,
  canChangeDefaultType,
  selectedTypeName,
  isLoading = false,
  error = false,
  disabled = false,
  onChange,
}: InstrumentTypeFieldProps) {
  const { t } = useFinancialInstrumentTranslation();

  const selectOptions: SelectOption[] = useMemo(
    () => options.map((o) => ({ value: o.id, label: o.name })),
    [options],
  );

  // Nombre a mostrar en el chip read-only: el explícito, o el del catálogo.
  const readonlyName =
    selectedTypeName || options.find((o) => o.id === value)?.name || DASH;

  if (canChangeDefaultType && options.length > 0) {
    return (
      <Select
        label={t('financial_instrument.field.type')}
        placeholder={t('financial_instrument.field.typePlaceholder')}
        options={selectOptions}
        value={value}
        required
        disabled={disabled || isLoading}
        error={error}
        errorMessage={error ? t('financial_instrument.validation.required') : undefined}
        onChange={(v) => onChange(String(v))}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary dark:text-dark-content-secondary">
        {t('financial_instrument.field.type')}
      </span>
      <div>
        <Badge color="zinc" label={readonlyName} />
      </div>
    </div>
  );
}
